/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { fetchAllAirtableRecords } from "./client";
import { joinDirectoryPath, slugify } from "./url";

import type { AirtableRecord } from "./client";
import type { ServicePartner, ServicePartnerCaseStudy } from "./types";

const BASE_ID = "appXeEl6DQgzqdZY1";
const TABLE_ID = "tblxZviZ09SRJVHIt";
// Only rows the SP team has marked production-ready. Unchecked rows stay
// internal to Airtable and never reach the directory.
const READY_FILTER = "{Ready for Directory}=TRUE()";

interface AirtableAttachment {
	id: string;
	width?: number;
	height?: number;
	url: string;
	filename?: string;
	thumbnails?: {
		small?: { url: string };
		large?: { url: string };
		full?: { url: string };
	};
}

interface ServicePartnerFields {
	"2026 Badge Rollup (from Organization Name)"?: string;
	"Company Name Rollup (from Organization Name)"?: string;
	"Partner Name"?: string;
	// Slug source of truth. `Slug (Override)` is a manual text field; when blank,
	// `Slug (Auto-generated-fallback)` is the formula-driven default off the name.
	"Slug (Override)"?: string;
	"Slug (Auto-generated-fallback)"?: string;
	Name?: string;
	"Company Highlights"?: string;
	Logo?: AirtableAttachment[];
	// Rollups "from Organization Name" come back as single-element string
	// arrays at runtime (verified against live data), not bare strings.
	"Website Rollup (from Organization Name)"?: string[];
	"Contact Us for directory"?: string;
	"Countries Supported"?: string[];
	"Office Locations"?: string[];
	"Languages Supported Rollup (from Organization Name)"?: string[];
	"Industries Supported Rollup (from Organization Name)"?: string[];
	"UE Skills"?: string[];
	"Project Types"?: string[];
	"Support Type"?: string[];
	// Up to two case studies, one indexed column set each.
	"Case Study 1 Title"?: string;
	"Case Study 1 Details"?: string;
	"Case Study 1 Image"?: AirtableAttachment[];
	"Case Study 1 Tags"?: string[];
	"Case Study 2 Title"?: string;
	"Case Study 2 Details"?: string;
	"Case Study 2 Image"?: AirtableAttachment[];
	"Case Study 2 Tags"?: string[];
}

/** First non-empty entry of a rollup string array, trimmed. */
function firstRollupValue(values: string[] | string | undefined): string | undefined {
	if (typeof values === "string") return values.trim() || undefined;
	if (!Array.isArray(values) || !values.length) return undefined;
	const first = values[0].trim();
	return first || undefined;
}

/**
 * Strip a single pair of wrapping double quotes. The industries rollup wraps
 * values that contain commas in literal quotes (e.g.
 * `"\"Architecture, Engineering, Construction\""`) — that's ONE industry, so
 * we must not split on the comma; just unwrap the quotes.
 */
function stripWrappingQuotes(value: string): string {
	const trimmed = value.trim();
	return trimmed.length >= 2 && trimmed.startsWith('"') && trimmed.endsWith('"') ? trimmed.slice(1, -1) : trimmed;
}

/** Build the case study at slot `n` (1-based); returns undefined when empty. */
function mapCaseStudyAt(f: ServicePartnerFields, n: 1 | 2): ServicePartnerCaseStudy | undefined {
	const title = f[`Case Study ${n} Title`]?.trim();
	const details = f[`Case Study ${n} Details`]?.trim();
	const image = f[`Case Study ${n} Image`];
	const imageUrl = image?.[0]?.thumbnails?.large?.url ?? image?.[0]?.url;
	const tags = f[`Case Study ${n} Tags`];
	if (!title && !details && !imageUrl && !(tags && tags.length > 0)) return undefined;
	return {
		title: title || undefined,
		details: details || undefined,
		imageUrl,
		tags: tags && tags.length > 0 ? tags : undefined,
	};
}

/**
 * Map the two indexed case-study column sets into a list, dropping empty slots
 * while preserving order (so slot 2 can be the only populated one). At most two.
 */
function mapCaseStudies(f: ServicePartnerFields): ServicePartnerCaseStudy[] {
	return ([1, 2] as const)
		.map((n) => mapCaseStudyAt(f, n))
		.filter((cs): cs is ServicePartnerCaseStudy => cs !== undefined);
}

/**
 * Map a single Airtable record to a `ServicePartner`. Returns `null` for rows
 * with no resolvable display name — same convention as the instructor /
 * training-partner fetchers so a blank `<h3>` never reaches the grid.
 */
function mapRecord(record: AirtableRecord<ServicePartnerFields>, basePath?: string): ServicePartner | null {
	const f = record.fields;
	// `Partner Name` wins when present; otherwise the resolved org name, then
	// the plain `Name` column as a last resort.
	const name =
		f["Partner Name"]?.trim() || firstRollupValue(f["Company Name Rollup (from Organization Name)"])?.trim() || "";
	if (!name) return null;

	// Slug is the trailing segment of the detail-page path and the key the detail
	// template matches the requested URL against. Source of truth is Airtable: the
	// manual `Slug (Override)` wins, else the formula-driven
	// `Slug (Auto-generated-fallback)`. We still run `slugify` over the chosen
	// value so a hand-typed override ("Acme Studios") can't emit an unsafe path
	// segment; only if both columns are empty (a row predating the slug fields) do
	// we fall back to slugifying the display name. `learnMoreUrl` is the full
	// path, built only when the caller supplied the directory's path (no path → no
	// link, rather than a hardcoded/fake prefix).
	const slug =
		slugify(f["Slug (Override)"]?.trim() || f["Slug (Auto-generated-fallback)"]?.trim() || "") || slugify(name);
	const learnMoreUrl = basePath && slug ? joinDirectoryPath(basePath, slug) : undefined;

	// `large` thumbnail (Airtable caps ≈ 512px) is the stable resized
	// derivative — fall through to a no-logo card if Airtable hasn't generated
	// one yet rather than handing the browser a full-resolution original.
	const logoUrl = f.Logo?.[0]?.thumbnails?.large?.url;

	return {
		id: record.id,
		name,
		slug,
		description: f["Company Highlights"]?.trim() || undefined,
		logoUrl,
		website: firstRollupValue(f["Website Rollup (from Organization Name)"]),
		email: f["Contact Us for directory"]?.trim() || undefined,
		countriesSupported: f["Countries Supported"] ?? [],
		officeLocations: f["Office Locations"] ?? [],
		languages: f["Languages Supported Rollup (from Organization Name)"] ?? [],
		// Strip the quote-wrapping the rollup adds around comma-containing
		// values; keep each array element as one industry (do NOT split).
		industries: (f["Industries Supported Rollup (from Organization Name)"] ?? []).map(stripWrappingQuotes),
		ueSkills: f["UE Skills"] ?? [],
		supportType: f["Support Type"] ?? [],
		caseStudies: mapCaseStudies(f),
		learnMoreUrl,
		badgeLevel: firstRollupValue(f["2026 Badge Rollup (from Organization Name)"]) || "",
	};
}

/**
 * Fetch every public service partner from the UE `New SP Brand Info` Airtable
 * tab.
 *
 * Filters to rows where `Ready for Directory` is checked. Requires
 * `AIRTABLE_SERVICE_PARTNER_PAT`; throws if missing (fail-loud so vault
 * misconfiguration is visible).
 *
 * @param basePath Optional directory page URL path used to build each
 *   partner's `learnMoreUrl` (`<basePath>/<slug>`). Omit when the caller only
 *   needs the card data and not the detail-page link.
 */
export async function fetchAllServicePartners(basePath?: string): Promise<ServicePartner[]> {
	const records = await fetchAllAirtableRecords<ServicePartnerFields>({
		baseId: BASE_ID,
		tableId: TABLE_ID,
		pat: process.env.AIRTABLE_SERVICE_PARTNER_PAT,
		filterByFormula: READY_FILTER,
		consumerLabel: "Service Partners",
		envVarName: "AIRTABLE_SERVICE_PARTNER_PAT",
	});

	return records.map((record) => mapRecord(record, basePath)).filter((p): p is ServicePartner => p !== null);
}

/**
 * Fetch the single service partner whose slug matches `slug`, or `null` when no
 * ready row matches. The detail page only needs one row, so this filters
 * Airtable server-side (`Ready for Directory` + slug match, capped at one
 * record) instead of pulling the whole directory and scanning in memory.
 *
 * Slug match mirrors `mapRecord`'s precedence — the manual `Slug (Override)`
 * when set, else `Slug (Auto-generated-fallback)` — and tolerates incidental
 * casing/whitespace in the stored value via `LOWER`/`TRIM`. The columns are
 * expected to already hold clean slugs (the fallback is a slugifying formula);
 * a row that predates the slug columns (both blank) isn't reachable here.
 *
 * `slugify` stays the authoritative transform: the Airtable filter only narrows
 * the fetch, then we confirm the mapped record's canonical slug equals the
 * requested one before returning it.
 *
 * @param basePath Optional directory page path used to build the partner's
 *   `learnMoreUrl` — pass the parent directory's path so it resolves back to
 *   this very detail page.
 */
export async function fetchServicePartnerBySlug(slug: string, basePath?: string): Promise<ServicePartner | null> {
	// Normalize first: `slugify` output is provably `[a-z0-9-]`, so it's safe to
	// inline into the formula string with no quote/backslash escaping needed.
	const normalized = slugify(slug);
	if (!normalized) return null;

	const slugSource = "IF({Slug (Override)},{Slug (Override)},{Slug (Auto-generated-fallback)})";
	const slugMatch = `LOWER(TRIM(${slugSource}))='${normalized}'`;

	const records = await fetchAllAirtableRecords<ServicePartnerFields>({
		baseId: BASE_ID,
		tableId: TABLE_ID,
		pat: process.env.AIRTABLE_SERVICE_PARTNER_PAT,
		filterByFormula: `AND(${READY_FILTER},${slugMatch})`,
		maxRecords: 1,
		consumerLabel: "Service Partner Detail",
		envVarName: "AIRTABLE_SERVICE_PARTNER_PAT",
	});

	const partner = records[0] ? mapRecord(records[0], basePath) : null;
	return partner && partner.slug === normalized ? partner : null;
}
